import React, { useState, useRef, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

export default function ImageResizer(props) {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [startHeight, setStartHeight] = useState(0);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // ✅ 초기 크기 설정 (HTML 속성에서 읽기)
  useEffect(() => {
    if (imgRef.current) {
      const width = props.node.attrs.width || imgRef.current.naturalWidth || 300;
      const height = props.node.attrs.height || imgRef.current.naturalHeight || 200;
      
      imgRef.current.style.width = `${width}px`;
      imgRef.current.style.height = `${height}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('.delete-btn')) return;

    e.preventDefault();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartY(e.clientY);
    setStartWidth(imgRef.current.width);
    setStartHeight(imgRef.current.height);
  };

  React.useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!imgRef.current || !isResizing) return;

      const deltaX = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);
      const aspectRatio = startWidth / startHeight;
      const newHeight = newWidth / aspectRatio;

      imgRef.current.style.width = `${newWidth}px`;
      imgRef.current.style.height = `${newHeight}px`;
    };

    const handleMouseUp = () => {
      // ✅ 마우스 업 시 크기를 노드 속성에 저장
      if (imgRef.current) {
        props.updateAttributes({
          width: Math.round(imgRef.current.width),
          height: Math.round(imgRef.current.height),
        });
      }
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, startY, startWidth, startHeight, props]);

  // 터치 지원 (모바일)
  const handleTouchStart = (e) => {
    if (e.target.closest('.delete-btn')) return;

    const touch = e.touches[0];
    setIsResizing(true);
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setStartWidth(imgRef.current.width);
    setStartHeight(imgRef.current.height);
  };

  React.useEffect(() => {
    if (!isResizing) return;

    const handleTouchMove = (e) => {
      if (!imgRef.current || !isResizing) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);
      const aspectRatio = startWidth / startHeight;
      const newHeight = newWidth / aspectRatio;

      imgRef.current.style.width = `${newWidth}px`;
      imgRef.current.style.height = `${newHeight}px`;
    };

    const handleTouchEnd = () => {
      // ✅ 터치 종료 시에도 크기 저장
      if (imgRef.current) {
        props.updateAttributes({
          width: Math.round(imgRef.current.width),
          height: Math.round(imgRef.current.height),
        });
      }
      setIsResizing(false);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isResizing, startX, startY, startWidth, startHeight, props]);

  return (
    <NodeViewWrapper as="div" style={{ position: 'relative' }}>
      <Box
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        sx={{
          position: 'relative',
          display: 'inline-block',
          cursor: isResizing ? 'grabbing' : 'grab',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 0 0 2px #1976d2',
            borderRadius: '8px',
          },
        }}
      >
        <img
          ref={imgRef}
          src={props.node.attrs.src}
          alt="첨부 이미지"
          style={{
            display: 'block',
            maxWidth: '100%',
            borderRadius: '8px',
            userSelect: 'none',
            pointerEvents: isResizing ? 'none' : 'auto',
          }}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
        />

        {/* 리사이즈 핸들 (오른쪽 아래) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '20px',
            height: '20px',
            backgroundColor: '#1976d2',
            borderRadius: '0 0 8px 0',
            cursor: isResizing ? 'grabbing' : 'grab',
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            '&::after': {
              content: '"⤡"',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
            },
          }}
        />

        {/* 삭제 버튼 */}
        <Tooltip title="이미지 삭제">
          <IconButton
            className="delete-btn"
            size="small"
            onClick={() => props.deleteNode()}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: '#f44336',
              color: 'white',
              width: 28,
              height: 28,
              borderRadius: '0 8px 0 0',
              '&:hover': {
                backgroundColor: '#d32f2f',
              },
              zIndex: 10,
            }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </NodeViewWrapper>
  );
}